import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);

/** Provides `{ groups, itemCount, add, remove, refresh, checkout }` to any
 *  component in the tree. Loads eagerly on mount for authenticated users so
 *  the badge count is accurate before the buyer opens the cart page. */
export function CartProvider({ children }) {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [itemCount, setItemCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setGroups([]); setItemCount(0); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/cart");
      setGroups(data.groups || []);
      setItemCount(data.item_count || 0);
      if (data.removed_count > 0) {
        toast.info(`${data.removed_count} item${data.removed_count === 1 ? "" : "s"} removed from cart — no longer available`);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const add = useCallback(async (listing_id) => {
    try {
      const { data } = await api.post("/cart/add", { listing_id });
      if (data.already_in_cart) {
        toast.info("Already in your cart.");
      } else {
        toast.success("Added to cart");
      }
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
  }, [refresh]);

  const remove = useCallback(async (listing_id) => {
    try {
      await api.delete(`/cart/${listing_id}`);
      refresh();
    } catch (err) { toast.error(formatErr(err)); }
  }, [refresh]);

  const checkout = useCallback(async (payment_method) => {
    try {
      const { data } = await api.post("/cart/checkout", {
        payment_method,
        accept_transaction_terms: true,
      });
      await refresh();
      return data.pos_created || [];
    } catch (err) {
      // Even on failure (e.g. an item was sold between load and checkout),
      // refresh so the buyer immediately sees the pruned cart and doesn't
      // resubmit the same stale request.
      await refresh();
      throw err;
    }
  }, [refresh]);

  return (
    <CartContext.Provider value={{ groups, itemCount, loading, refresh, add, remove, checkout }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
